// components/NativeLayerCutOverlay.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, RotateCcw, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import type { CropPoint, Rect } from '#types/types';
import {
  buildNativeLayerCutPreviewFrameGuardMask,
  clampNativeLayerCutBlendRadius,
  computeCutBlendDimAlpha,
  createCenterSquarePolygon,
  getNativeLayerCutImageBounds,
  getNativeLayerCutPreviewDimensions,
  getNativeLayerCutPreviewRadius,
  NATIVE_LAYER_CUT_DEFAULT_BLEND_RADIUS,
  normalizePolygonClockwise,
  renderKeepAreaPreview,
} from '#lib/layer-cut-utils';
import { getProjectPanOffset, getProjectZoomLevel } from '#lib/project-runtime-layer-ui-state';
import {
  buildStudioLayerRectPreservingTransformPivot,
  getTransformedRectBounds,
  projectStudioLayerPointToLocal,
  projectStudioLayerPointToWorld,
  resolveStudioLayerTransform,
} from '#lib/studio-layer-transform';
import { buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import LayerSelectionFrame from './LayerSelectionFrame';

/*
 * Pattern: keep interaction canvas inside the zoomed layer, but portal floating HTML UI
 * outside the transformed viewport subtree (canvas container root). This avoids the
 * "scale + counter-scale" raster/compositor artifacts on text/buttons after zoom.
 * Position portaled UI in screen space from world coords: screen = pan + world * zoom.
 */

const SNAP_THRESHOLD = 32;
const CUT_TOOLBAR_MARGIN_PX = 8;
const CUT_TOOLBAR_WIDTH_PX = 150;
const CUT_TOOLBAR_HEIGHT_PX = 88;
const DEFAULT_PAN_OFFSET = { x: 0, y: 0 } as const;
const LIVE_VIEWPORT_TRANSFORM = 'translate(var(--viewport-live-pan-x, 0px), var(--viewport-live-pan-y, 0px)) scale(var(--viewport-live-zoom, 1))';

const ROOT_OVERLAY_STYLE: React.CSSProperties = { zIndex: 35, pointerEvents: 'none' };
const FLOATING_UI_STYLE: React.CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 80 };
const OVERLAY_SVG_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'visible',
  transform: LIVE_VIEWPORT_TRANSFORM,
  transformOrigin: '0 0',
};

const translations = {
  en: {
    validateTitle: 'Validate cut',
    resetTitle: 'Reset cut',
    cancelTitle: 'Cancel cut',
    doubleClickHint: 'Double-click to finish here',
    altPeekHint: 'Hold Alt to preview what is behind',
    keepInsideTitle: 'Keep inside',
    keepOutsideTitle: 'Keep outside',
    edgeSoftness: 'Edge softness',
  },
  fr: {
    validateTitle: 'Valider la découpe',
    resetTitle: 'Réinitialiser la découpe',
    cancelTitle: 'Annuler la découpe',
    doubleClickHint: 'Double-cliquez pour terminer ici',
    altPeekHint: 'Maintenez Alt pour voir ce qu\'il y a derrière',
    keepInsideTitle: 'Conserver l’intérieur',
    keepOutsideTitle: 'Conserver l’extérieur',
    edgeSoftness: 'Adoucissement du bord',
  },
} as const;

interface NativeLayerCutOverlayProps {
  layerId: string;
  workspaceId: string;
}

interface OverlayActionButtonProps {
  onClick: () => void;
  title: string;
  icon: React.ComponentType<{ size?: number }>;
  isActive?: boolean;
  disabled?: boolean;
}

const OverlayActionButton: React.FC<OverlayActionButtonProps> = React.memo(({
  onClick,
  title,
  icon: Icon,
  isActive = false,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const stopEventPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const buttonStyle = useMemo<React.CSSProperties>(() => ({
    width: '30px',
    height: '30px',
    minWidth: '30px',
    padding: '6px',
    color: isActive ? '#0D0D0D' : (isHovered ? 'white' : '#B4B4B4'),
    backgroundColor: isActive ? '#ECECEC' : (isHovered ? '#2A2A2A' : 'rgba(255, 255, 255, 0.08)'),
  }), [isActive, isHovered]);

  return (
    <button
      onClick={onClick}
      onMouseDown={stopEventPropagation}
      onTouchStart={stopEventPropagation}
      title={title}
      type="button"
      disabled={disabled}
      className="flex items-center justify-center rounded transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
      style={buttonStyle}
      aria-label={title}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Icon size={18} />
    </button>
  );
});

OverlayActionButton.displayName = 'OverlayActionButton';

const KeepInsideIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 9V5a2 2 0 0 1 2-2h4M15 3h4a2 2 0 0 1 2 2v4M21 15v4a2 2 0 0 1-2 2h-4M9 21H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" fillOpacity="0.75" />
  </svg>
);

const KeepOutsideIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 9V5a2 2 0 0 1 2-2h4M15 3h4a2 2 0 0 1 2 2v4M21 15v4a2 2 0 0 1-2 2h-4M9 21H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M5 5h14v14H5z M8 8h8v8H8z" fill="currentColor" fillOpacity="0.75" fillRule="evenodd" clipRule="evenodd" />
  </svg>
);

interface CutBlendScratch {
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  maskCtx: CanvasRenderingContext2D | null;
  keptMask: Uint8ClampedArray;
  dimImage: ImageData;
  dimScratch: {
    dim: Uint8ClampedArray;
    horizontal: Uint8ClampedArray;
    blurred: Uint8ClampedArray;
  };
  frameGuardMask: Uint8Array;
}

const ensureCutBlendScratch = (
  scratch: CutBlendScratch | null,
  width: number,
  height: number,
): CutBlendScratch => {
  if (scratch && scratch.width === width && scratch.height === height) {
    return scratch;
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  // The mask is read back every preview tick, so favor software rasterization over GPU readback.
  return {
    width,
    height,
    canvas,
    maskCtx: canvas.getContext('2d', { willReadFrequently: true }),
    keptMask: new Uint8ClampedArray(width * height),
    dimImage: new ImageData(width, height),
    dimScratch: {
      dim: new Uint8ClampedArray(width * height),
      horizontal: new Uint8ClampedArray(width * height),
      blurred: new Uint8ClampedArray(width * height),
    },
    frameGuardMask: new Uint8Array(width * height),
  };
};

const buildKeepAreaPath = (
  polygons: CropPoint[][],
  scaleX = 1,
  scaleY = 1,
): Path2D => {
  const path = new Path2D();
  for (const polygon of polygons) {
    const normalized = normalizePolygonClockwise(polygon);
    const first = normalized[0];
    if (!first) continue;
    path.moveTo(first.x * scaleX, first.y * scaleY);
    for (let index = 1; index < normalized.length; index += 1) {
      const point = normalized[index];
      if (point) path.lineTo(point.x * scaleX, point.y * scaleY);
    }
    path.closePath();
  }
  return path;
};

interface CutBlendPreviewGeometry {
  layerRect: Rect;
  layerWorldRect: Rect;
  frameRect: Rect | null;
  transform: ReturnType<typeof resolveStudioLayerTransform>;
  layerScale: number;
}

const renderCutBlendPreview = (
  ctx: CanvasRenderingContext2D,
  scratch: CutBlendScratch,
  source: CanvasImageSource,
  width: number,
  height: number,
  polygons: CropPoint[][],
  invert: boolean,
  blendRadius: number,
  geometry: CutBlendPreviewGeometry,
): void => {
  const maskCtx = scratch.maskCtx;
  if (!maskCtx) return;

  const previewWidth = scratch.width;
  const previewHeight = scratch.height;
  const scaleX = previewWidth / width;
  const scaleY = previewHeight / height;
  const cutBounds = getNativeLayerCutImageBounds(width, height, polygons, invert);
  if (!cutBounds) return;

  maskCtx.clearRect(0, 0, previewWidth, previewHeight);
  maskCtx.drawImage(source, 0, 0, previewWidth, previewHeight);
  const sourcePixels = maskCtx.getImageData(0, 0, previewWidth, previewHeight).data;
  const keepPath = buildKeepAreaPath(polygons, scaleX, scaleY);

  maskCtx.clearRect(0, 0, previewWidth, previewHeight);
  maskCtx.fillStyle = '#fff';
  if (invert) {
    maskCtx.fillRect(0, 0, previewWidth, previewHeight);
    maskCtx.globalCompositeOperation = 'destination-out';
    maskCtx.fill(keepPath);
    maskCtx.globalCompositeOperation = 'source-over';
  } else {
    maskCtx.fill(keepPath);
  }

  const maskPixels = maskCtx.getImageData(0, 0, previewWidth, previewHeight).data;
  for (let i = 0; i < scratch.keptMask.length; i += 1) {
    scratch.keptMask[i] = (maskPixels[i * 4 + 3] ?? 0) >= 128 ? 255 : 0;
  }

  const cutOffsetX = invert ? 0 : Math.floor(cutBounds.minX * scaleX);
  const cutOffsetY = invert ? 0 : Math.floor(cutBounds.minY * scaleY);
  const cutWidth = invert
    ? previewWidth
    : Math.max(1, Math.min(previewWidth - cutOffsetX, Math.floor(cutBounds.width * scaleX)));
  const cutHeight = invert
    ? previewHeight
    : Math.max(1, Math.min(previewHeight - cutOffsetY, Math.floor(cutBounds.height * scaleY)));
  let minOpaqueX = cutWidth;
  let minOpaqueY = cutHeight;
  let maxOpaqueX = -1;
  let maxOpaqueY = -1;
  for (let y = 0; y < previewHeight; y += 1) {
    for (let x = 0; x < previewWidth; x += 1) {
      const cutX = x - cutOffsetX;
      const cutY = y - cutOffsetY;
      if (cutX < 0 || cutX >= cutWidth || cutY < 0 || cutY >= cutHeight) continue;
      const pixelIndex = y * previewWidth + x;
      if (scratch.keptMask[pixelIndex] === 0 || (sourcePixels[pixelIndex * 4 + 3] ?? 0) === 0) continue;
      minOpaqueX = Math.min(minOpaqueX, cutX);
      minOpaqueY = Math.min(minOpaqueY, cutY);
      maxOpaqueX = Math.max(maxOpaqueX, cutX);
      maxOpaqueY = Math.max(maxOpaqueY, cutY);
    }
  }
  const hasOpaquePixels = maxOpaqueX >= 0 && maxOpaqueY >= 0;
  const transparentMargins = {
    left: hasOpaquePixels ? minOpaqueX : cutWidth,
    top: hasOpaquePixels ? minOpaqueY : cutHeight,
    right: hasOpaquePixels ? cutWidth - 1 - maxOpaqueX : cutWidth,
    bottom: hasOpaquePixels ? cutHeight - 1 - maxOpaqueY : cutHeight,
  };

  const previewRadius = getNativeLayerCutPreviewRadius(blendRadius, width, previewWidth);
  const postCutLayerRect = invert
    ? geometry.layerRect
    : buildStudioLayerRectPreservingTransformPivot(
        geometry.layerRect,
        { x: cutBounds.minX * geometry.layerScale, y: cutBounds.minY * geometry.layerScale },
        cutBounds.width,
        cutBounds.height,
        geometry.layerScale,
        geometry.layerWorldRect,
        geometry.transform,
        geometry.frameRect ?? { x: 0, y: 0 },
      );
  const postCutLayerWorldRect = geometry.frameRect
    ? {
        ...postCutLayerRect,
        x: geometry.frameRect.x + postCutLayerRect.x,
        y: geometry.frameRect.y + postCutLayerRect.y,
      }
    : postCutLayerRect;
  const frameGuardMask = geometry.frameRect
    ? buildNativeLayerCutPreviewFrameGuardMask({
        width: previewWidth,
        height: previewHeight,
        cutWidth,
        cutHeight,
        cutOffsetX,
        cutOffsetY,
        postCutLayerRect: postCutLayerWorldRect,
        frameRect: geometry.frameRect,
        transform: geometry.transform,
        layerScale: geometry.layerScale,
        guardBandPx: previewRadius,
        transparentMargins,
        targetMask: scratch.frameGuardMask,
      })
    : undefined;
  const dim = computeCutBlendDimAlpha(
    scratch.keptMask,
    previewWidth,
    previewHeight,
    previewRadius,
    0.7,
    frameGuardMask,
    scratch.dimScratch,
  );
  const dimData = scratch.dimImage.data;
  for (let i = 0; i < dim.length; i += 1) {
    dimData[i * 4 + 3] = dim[i] ?? 0;
  }

  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  maskCtx.putImageData(scratch.dimImage, 0, 0);
  ctx.drawImage(scratch.canvas, 0, 0, width, height);
};

const NativeLayerCutOverlay: React.FC<NativeLayerCutOverlayProps> = React.memo(({ layerId, workspaceId }) => {
  const { kernel } = useKernel();
  const projectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const layerTrigger = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId] ?? null);
  const layer = useMemo(() => {
    if (!kernel.current) return null;
    return kernel.current.getLayerRegistry()[layerId] ?? null;
  }, [kernel, layerId, layerTrigger]);
  const session = useKernelStore((state) => state.nativeLayerCutSession);
  const zoomLevel = useKernelStore((state) =>
    projectId
      ? (() => {
          const runtime = state.inventory.Project.registry[projectId]?.runtime;
          const value = runtime?.zoomLevel;
          return typeof value === 'number' && Number.isFinite(value) ? value : 1;
        })()
      : 1
  );
  const panOffset = useKernelStore((state) =>
    projectId
      ? (() => {
          const runtime = state.inventory.Project.registry[projectId]?.runtime;
          const value = runtime?.panOffset;
          if (
            value &&
            typeof value === 'object' &&
            Number.isFinite((value as { x?: unknown }).x) &&
            Number.isFinite((value as { y?: unknown }).y)
          ) {
            return value as { x: number; y: number };
          }
          return DEFAULT_PAN_OFFSET;
        })()
      : DEFAULT_PAN_OFFSET
  );
  const isViewportPanning = useKernelStore((state) =>
    projectId ? state.inventory.Project.registry[projectId]?.runtime?.isPanning === true : false
  );
  const workspaceFrameRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect ?? null
  );
  const language = useKernelStore((state) => state.language);
  const isAltKeyPressed = useKernelStore((state) => state.isAltKeyPressed);
  const t = translations[language];
  const isSessionActive = session?.layerId === layerId && session.workspaceId === workspaceId;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cutBlendScratchRef = useRef<CutBlendScratch | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const currentPolygonRef = useRef<CropPoint[]>([]);
  const mouseMoveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [polygons, setPolygons] = useState<CropPoint[][]>([]);
  const [currentPolygon, setCurrentPolygon] = useState<CropPoint[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mousePos, setMousePos] = useState<CropPoint | null>(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const [showDoubleClickHint, setShowDoubleClickHint] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [blendRadius, setBlendRadius] = useState(NATIVE_LAYER_CUT_DEFAULT_BLEND_RADIUS);
  const [invert, setInvert] = useState(false);

  useEffect(() => {
    currentPolygonRef.current = currentPolygon;
  }, [currentPolygon]);

  const sourceCanvas = useMemo(() => {
    if (!layer?.imageData) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = layer.imageData.width;
    canvas.height = layer.imageData.height;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    context.putImageData(layer.imageData, 0, 0);
    return canvas;
  }, [layer?.imageData, layer?.contentVersion]);

  const hideHintAndClearTimer = useCallback(() => {
    if (mouseMoveTimerRef.current) {
      clearTimeout(mouseMoveTimerRef.current);
      mouseMoveTimerRef.current = null;
    }
    setShowDoubleClickHint(false);
  }, []);

  const resetLocalState = useCallback(() => {
    hideHintAndClearTimer();
    setPolygons([]);
    setCurrentPolygon([]);
    setIsDrawing(false);
    setMousePos(null);
    setIsSnapping(false);
    setShowDoubleClickHint(false);
  }, [hideHintAndClearTimer]);

  useEffect(() => {
    if (isSessionActive) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    resetLocalState();
  }, [isSessionActive, resetLocalState]);

  const getLayerCoordinates = useCallback((clientX: number, clientY: number): CropPoint | null => {
    const canvasContainer = kernel.current?.canvasContainerRef.current;
    const imageData = layer?.imageData;
    const frameRect = workspaceFrameRect;
    if (!kernel.current || !canvasContainer || !imageData || !frameRect || !projectId) {
      return null;
    }

    // Read the viewport at execution time: a wheel zoom updates the DOM transform before this
    // component re-renders, so a render-captured zoom/pan would place the point off-target.
    const viewportZoom = getProjectZoomLevel(kernel.current, projectId);
    const viewportPan = getProjectPanOffset(kernel.current, projectId);

    const containerRect = canvasContainer.getBoundingClientRect();
    if (containerRect.width <= 0 || containerRect.height <= 0) {
      return null;
    }

    const safeZoom = Math.max(viewportZoom, 0.0001);
    const layerScale = Math.max(layer.scale ?? 1, 0.0001);
    const layerWorldRect = {
      x: frameRect.x + layer.rect.x,
      y: frameRect.y + layer.rect.y,
      w: imageData.width * layerScale,
      h: imageData.height * layerScale,
    };
    const transform = resolveStudioLayerTransform(layer.params);

    const worldPoint = {
      x: (clientX - containerRect.left - viewportPan.x) / safeZoom,
      y: (clientY - containerRect.top - viewportPan.y) / safeZoom,
    };
    const localPoint = projectStudioLayerPointToLocal(worldPoint, layerWorldRect, transform);

    return { x: localPoint.x / layerScale, y: localPoint.y / layerScale };
  }, [kernel, layer?.imageData, layer?.params, layer?.rect.x, layer?.rect.y, layer?.scale, projectId, workspaceFrameRect]);

  const finalizePolygon = useCallback((polygonOverride?: CropPoint[]) => {
    const polygonToFinalize = polygonOverride ?? currentPolygonRef.current;
    if (polygonToFinalize.length < 3) {
      return;
    }
    hideHintAndClearTimer();
    setPolygons((previous) => [...previous, [...polygonToFinalize]]);
    setCurrentPolygon([]);
    setIsDrawing(false);
    setIsSnapping(false);
  }, [hideHintAndClearTimer]);

  const cancelCurrentPolygon = useCallback(() => {
    hideHintAndClearTimer();
    setCurrentPolygon([]);
    setIsDrawing(false);
    setIsSnapping(false);
  }, [hideHintAndClearTimer]);

  const handleReset = useCallback(() => {
    resetLocalState();
  }, [resetLocalState]);

  const handleCancel = useCallback(() => {
    kernel.current?.cancelNativeLayerCut();
  }, [kernel]);

  const handleBlendRadiusChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBlendRadius(clampNativeLayerCutBlendRadius(Number(event.currentTarget.value)));
  }, []);

  const handleInvertToggle = useCallback(() => {
    setInvert((previous) => !previous);
  }, []);

  const handleValidate = useCallback(async () => {
    if (!kernel.current || !layer?.imageData || isApplying) {
      return;
    }

    const hasPendingPolygon = currentPolygon.length > 2;
    const hasCommittedPolygon = polygons.length > 0;

    let polygonsToApply: CropPoint[][];
    if (hasPendingPolygon || hasCommittedPolygon) {
      polygonsToApply = hasPendingPolygon ? [...polygons, [...currentPolygon]] : polygons;
    } else {
      polygonsToApply = [createCenterSquarePolygon(layer.imageData.width, layer.imageData.height)];
    }

    setIsApplying(true);
    try {
      const applied = await kernel.current.applyNativeLayerCut(layerId, polygonsToApply, {
        blendRadius,
        invert,
      });
      if (applied) {
        kernel.current.cancelNativeLayerCut();
      }
    } finally {
      setIsApplying(false);
    }
  }, [blendRadius, currentPolygon, invert, isApplying, kernel, layer?.imageData, layerId, polygons]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isSessionActive || event.button !== 0) {
      return;
    }
    const point = getLayerCoordinates(event.clientX, event.clientY);
    if (!point) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    hideHintAndClearTimer();

    if (!isDrawing && isSnapping && currentPolygonRef.current.length > 2) {
      const firstPoint = currentPolygonRef.current[0];
      if (firstPoint) {
        finalizePolygon([...currentPolygonRef.current, firstPoint]);
        return;
      }
    }

    pointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDrawing(true);
    setCurrentPolygon((previous) => [...previous, point]);
  }, [finalizePolygon, getLayerCoordinates, hideHintAndClearTimer, isDrawing, isSessionActive, isSnapping]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isSessionActive || isViewportPanning || event.buttons === 4) {
      return;
    }

    const point = getLayerCoordinates(event.clientX, event.clientY);
    if (!point) {
      return;
    }

    event.stopPropagation();
    hideHintAndClearTimer();
    setMousePos(point);

    const livePolygon = currentPolygonRef.current;
    if (livePolygon.length > 2 && !isDrawing) {
      mouseMoveTimerRef.current = setTimeout(() => {
        setShowDoubleClickHint(true);
      }, 500);
    }

    if (livePolygon.length > 2) {
      const firstPoint = livePolygon[0];
      if (firstPoint) {
        // Points are stored in image pixels, but the magnet is perceived in screen pixels.
        // Convert the fixed screen-space radius back through both layer scale and viewport zoom.
        const viewportZoom = Math.max(projectId ? getProjectZoomLevel(kernel.current!, projectId) : 1, 0.0001);
        const currentLayer = kernel.current?.getLayerRegistry()[layerId];
        const layerScale = Math.max(currentLayer?.scale ?? 1, 0.0001);
        const snapThreshold = SNAP_THRESHOLD / (viewportZoom * layerScale);
        const distance = Math.hypot(point.x - firstPoint.x, point.y - firstPoint.y);
        setIsSnapping(distance < snapThreshold);
      }
    } else {
      setIsSnapping(false);
    }

    if (!isDrawing) {
      return;
    }

    setCurrentPolygon((previous) => [...previous, point]);
  }, [getLayerCoordinates, hideHintAndClearTimer, isDrawing, isSessionActive, isViewportPanning, layerId, projectId]);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isSessionActive) {
      return;
    }
    if (event.type === 'pointerup' && event.button !== 0) {
      return;
    }
    event.stopPropagation();
    hideHintAndClearTimer();

    if (pointerIdRef.current === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      pointerIdRef.current = null;
    }

    if (isDrawing && isSnapping) {
      const firstPoint = currentPolygonRef.current[0];
      if (firstPoint) {
        finalizePolygon([...currentPolygonRef.current, firstPoint]);
        return;
      }
    }
    setIsDrawing(false);
  }, [finalizePolygon, hideHintAndClearTimer, isDrawing, isSessionActive, isSnapping]);

  const handleDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isSessionActive) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (currentPolygonRef.current.length > 2) {
      finalizePolygon();
    }
  }, [finalizePolygon, isSessionActive]);

  const stopPointerPropagation = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleInteractionMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
  }, []);

  const interactionLayerStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'auto',
    zIndex: 70,
    cursor: isSnapping ? 'pointer' : 'crosshair',
    userSelect: 'none',
    touchAction: 'none',
  }), [isSnapping]);

  useEffect(() => {
    if (!isSessionActive) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!kernel.current || isInertSurface(kernel.current.canvasContainerRef.current)) return;
      if (event.key === 'Escape') {
        if (currentPolygonRef.current.length > 0) {
          cancelCurrentPolygon();
          return;
        }
        kernel.current.cancelNativeLayerCut();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [cancelCurrentPolygon, isSessionActive, kernel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layer?.imageData) {
      return;
    }

    const width = layer.imageData.width;
    const height = layer.imageData.height;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    context.clearRect(0, 0, width, height);

    if (!isAltKeyPressed && sourceCanvas && polygons.length > 0) {
      if (blendRadius > 0) {
        const previewDimensions = getNativeLayerCutPreviewDimensions(width, height);
        const scratch = ensureCutBlendScratch(
          cutBlendScratchRef.current,
          previewDimensions.width,
          previewDimensions.height,
        );
        cutBlendScratchRef.current = scratch;
        const layerScale = layer.scale ?? 1;
        const frameRect = workspaceFrameRect;
        const layerWorldRect = {
          x: (frameRect?.x ?? 0) + layer.rect.x,
          y: (frameRect?.y ?? 0) + layer.rect.y,
          w: Number.isFinite(layer.rect.w) && layer.rect.w > 0
            ? layer.rect.w
            : width * layerScale,
          h: Number.isFinite(layer.rect.h) && layer.rect.h > 0
            ? layer.rect.h
            : height * layerScale,
        };
        renderCutBlendPreview(
          context,
          scratch,
          sourceCanvas,
          width,
          height,
          polygons,
          invert,
          blendRadius,
          {
            layerRect: layer.rect,
            layerWorldRect,
            frameRect,
            transform: resolveStudioLayerTransform(layer.params),
            layerScale,
          },
        );
      } else {
        renderKeepAreaPreview(context, sourceCanvas, width, height, polygons, 0.7, invert);
      }
    }
  }, [blendRadius, invert, layer, layer?.imageData, polygons, isAltKeyPressed, sourceCanvas, workspaceFrameRect]);

  useEffect(() => () => {
    if (mouseMoveTimerRef.current) {
      clearTimeout(mouseMoveTimerRef.current);
    }
  }, []);

  if (!isSessionActive || !layer?.imageData) {
    return null;
  }

  const safeZoom = Math.max(zoomLevel, 0.0001);
  const layerScale = Math.max(layer.scale ?? 1, 0.0001);
  const baseFrameX = workspaceFrameRect?.x ?? 0;
  const baseFrameY = workspaceFrameRect?.y ?? 0;
  const layerWorldRect = {
    x: baseFrameX + layer.rect.x,
    y: baseFrameY + layer.rect.y,
    w: layer.imageData.width * layerScale,
    h: layer.imageData.height * layerScale,
  };
  const layerTransform = resolveStudioLayerTransform(layer.params);
  const transformedLayerWorldBounds = getTransformedRectBounds(layerWorldRect, layerTransform);
  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  const overlayStrokeWidth = 2 / safeZoom;
  const firstPointRadius = (isSnapping ? 10 : 5) / safeZoom;

  const toWorldPoint = (point: CropPoint): CropPoint => projectStudioLayerPointToWorld(
    { x: point.x * layerScale, y: point.y * layerScale },
    layerWorldRect,
    layerTransform,
  );

  const currentPolygonWorld = currentPolygon.map(toWorldPoint);
  const mouseWorldPoint = mousePos ? toWorldPoint(mousePos) : null;
  const firstPointWorld = currentPolygonWorld[0] ?? null;
  const lastPointWorld = currentPolygonWorld[currentPolygonWorld.length - 1] ?? null;
  const targetPreviewPointWorld = isViewportPanning ? null : ((isSnapping && firstPointWorld) ? firstPointWorld : mouseWorldPoint);

  const viewportWidth = portalHost?.clientWidth ?? 0;
  const viewportHeight = portalHost?.clientHeight ?? 0;
  const transformedLayerLeft = panOffset.x + transformedLayerWorldBounds.x * zoomLevel;
  const transformedLayerRight = panOffset.x + (transformedLayerWorldBounds.x + transformedLayerWorldBounds.w) * zoomLevel;
  const preferredToolbarLeft = transformedLayerRight + CUT_TOOLBAR_MARGIN_PX;
  const fallbackToolbarLeft = transformedLayerLeft - CUT_TOOLBAR_MARGIN_PX - CUT_TOOLBAR_WIDTH_PX;
  const hasViewportSize = viewportWidth > 0 && viewportHeight > 0;
  const toolbarLeft = hasViewportSize && preferredToolbarLeft + CUT_TOOLBAR_WIDTH_PX > viewportWidth - CUT_TOOLBAR_MARGIN_PX
    ? fallbackToolbarLeft
    : preferredToolbarLeft;
  const toolbarStyle = {
    position: 'absolute' as const,
    left: hasViewportSize
      ? `${Math.max(CUT_TOOLBAR_MARGIN_PX, Math.min(toolbarLeft, viewportWidth - CUT_TOOLBAR_WIDTH_PX - CUT_TOOLBAR_MARGIN_PX))}px`
      : buildLiveScreenX(transformedLayerWorldBounds.x + transformedLayerWorldBounds.w, panOffset.x, zoomLevel),
    top: hasViewportSize
      ? `${Math.max(CUT_TOOLBAR_MARGIN_PX, Math.min(
          panOffset.y + transformedLayerWorldBounds.y * zoomLevel + CUT_TOOLBAR_MARGIN_PX,
          viewportHeight - CUT_TOOLBAR_HEIGHT_PX - CUT_TOOLBAR_MARGIN_PX,
        ))}px`
      : buildLiveScreenY(transformedLayerWorldBounds.y, panOffset.y, zoomLevel),
  };

  const floatingUi = (
    <div style={FLOATING_UI_STYLE}>
      <div
        className="pointer-events-auto"
        style={toolbarStyle}
        onPointerDown={stopPointerPropagation}
      >
        <div className="flex w-fit flex-col gap-1 rounded-lg bg-[#2A2A2A] p-1 text-xs text-white shadow-lg">
          <div className="flex items-center justify-end gap-1" title={t.edgeSoftness}>
            <input
              type="number"
              min={0}
              max={64}
              step={1}
              value={blendRadius}
              onChange={handleBlendRadiusChange}
              className="w-12 bg-transparent px-1 py-1 text-left text-white outline-none"
              aria-label={t.edgeSoftness}
            />
            <span className="text-white/50">px</span>
          </div>
          <div className="flex items-center justify-end gap-1">
            <OverlayActionButton
              onClick={handleInvertToggle}
              title={invert ? t.keepOutsideTitle : t.keepInsideTitle}
              icon={invert ? KeepOutsideIcon : KeepInsideIcon}
              isActive={invert}
            />
            <OverlayActionButton
              onClick={handleValidate}
              title={t.validateTitle}
              icon={Check}
              isActive={true}
              disabled={isApplying}
            />
            {(polygons.length > 0 || currentPolygon.length > 0) && (
              <OverlayActionButton
                onClick={handleReset}
                title={t.resetTitle}
                icon={RotateCcw}
              />
            )}
            <OverlayActionButton
              onClick={handleCancel}
              title={t.cancelTitle}
              icon={X}
            />
          </div>
        </div>
      </div>

      {showDoubleClickHint && (
        <div
          className="px-3 py-1.5 rounded-md bg-neutral-900/80 text-white text-sm pointer-events-none shadow-lg backdrop-blur-sm"
          style={{
            position: 'absolute',
            left: buildLiveScreenX(transformedLayerWorldBounds.x + (transformedLayerWorldBounds.w / 2), panOffset.x, zoomLevel),
            top: `calc(${buildLiveScreenY(transformedLayerWorldBounds.y + transformedLayerWorldBounds.h, panOffset.y, zoomLevel)} - 12px)`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {t.doubleClickHint}
        </div>
      )}

      <div
        className="px-3 py-1.5 rounded-md bg-neutral-900/80 text-white text-sm pointer-events-none shadow-lg backdrop-blur-sm"
        style={{
          position: 'absolute',
          left: buildLiveScreenX(transformedLayerWorldBounds.x + (transformedLayerWorldBounds.w / 2), panOffset.x, zoomLevel),
          top: `calc(${buildLiveScreenY(transformedLayerWorldBounds.y + transformedLayerWorldBounds.h, panOffset.y, zoomLevel)} - 12px)`,
          transform: 'translate(-50%, -100%)',
        }}
      >
        {session?.instruction ?? t.altPeekHint}
      </div>
    </div>
  );

  const interactionLayer = (
    <div
      style={interactionLayerStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleInteractionMouseDown}
    >
      <svg
        width="100%"
        height="100%"
        style={OVERLAY_SVG_STYLE}
      >
        {currentPolygonWorld.length > 0 && (
          <polyline
            points={currentPolygonWorld.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke="rgba(255, 255, 255, 0.9)"
            strokeWidth={overlayStrokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {lastPointWorld && targetPreviewPointWorld && (
          <polyline
            points={
              firstPointWorld
                ? `${lastPointWorld.x},${lastPointWorld.y} ${targetPreviewPointWorld.x},${targetPreviewPointWorld.y} ${firstPointWorld.x},${firstPointWorld.y}`
                : `${lastPointWorld.x},${lastPointWorld.y} ${targetPreviewPointWorld.x},${targetPreviewPointWorld.y}`
            }
            fill="none"
            stroke="rgba(255, 255, 255, 0.7)"
            strokeWidth={overlayStrokeWidth}
            strokeDasharray={`${4 / safeZoom} ${4 / safeZoom}`}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {firstPointWorld && (
          <>
            <circle
              cx={firstPointWorld.x}
              cy={firstPointWorld.y}
              r={firstPointRadius}
              fill={`rgba(255, 255, 255, ${isSnapping ? 1 : 0.7})`}
            />
            <circle
              cx={firstPointWorld.x}
              cy={firstPointWorld.y}
              r={firstPointRadius}
              fill="none"
              stroke={`rgba(0, 0, 0, ${(isSnapping ? 1 : 0.7) * 0.8})`}
              strokeWidth={overlayStrokeWidth}
            />
          </>
        )}
      </svg>
    </div>
  );

  return (
    <div
      className="absolute inset-0"
      style={ROOT_OVERLAY_STYLE}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
      <LayerSelectionFrame layerId={layerId} showDimensionPill={false} />
      {portalHost ? createPortal(interactionLayer, portalHost) : null}
      {portalHost ? createPortal(floatingUi, portalHost) : null}
    </div>
  );
});

NativeLayerCutOverlay.displayName = 'NativeLayerCutOverlay';

export default NativeLayerCutOverlay;

